/**
 * FleetProvider — monta el store de la flota y el motor que lo alimenta.
 *
 * La forma del componente es deliberada y es la mitad del trabajo de esta fase:
 *
 *   <FleetContext.Provider value={storeEstable}>
 *     <FleetEngineRunner />   ← corre el motor, renderiza null
 *     {children}              ← la app entera
 *   </FleetContext.Provider>
 *
 * El motor NO corre en el provider sino en un hermano de `children`. Da igual
 * cuántas veces se re-renderice el runner (el hook de WS tiene estado propio,
 * y `tracePositions` cambia con los ajustes): `children` es la misma
 * referencia de elemento y React salta su subárbol entero. El valor del
 * contexto es el store, creado una vez, así que tampoco lo invalida nada.
 *
 * Resultado: un mensaje WS de un vehículo no re-renderiza NADA por encima de
 * los componentes suscritos a ese IMEI.
 */

import { useMemo, useRef } from "react";
import { FleetContext } from "../hooks/useFleet.js";
import { createFleetStore } from "../lib/fleet/fleetStore.js";
import { useFleetEngine } from "../hooks/useFleetEngine.js";

/** Corre el motor contra el store del contexto. No pinta nada. */
function FleetEngineRunner({ store, tracePositions }) {
  useFleetEngine(store, { tracePositions });
  return null;
}

export function FleetProvider({ children, tracePositions = 30 }) {
  const storeRef = useRef(null);
  if (storeRef.current === null) storeRef.current = createFleetStore();
  const store = storeRef.current;

  // Memo por si el padre re-renderizara: `store` es estable de por vida, así
  // que esto nunca produce un valor nuevo — es una salvaguarda explícita, no
  // una optimización marginal. Un valor nuevo acá invalidaría todas las
  // suscripciones de golpe.
  const value = useMemo(() => store, [store]);

  return (
    <FleetContext.Provider value={value}>
      <FleetEngineRunner store={store} tracePositions={tracePositions} />
      {children}
    </FleetContext.Provider>
  );
}

export default FleetProvider;
